<template>
  <div>
    <div class="column">
      <label>offer for customer</label>
      <input
        type="text"
        class="item-name-input"
        :class="{ error: !digitalVoucherDraftState.item_name }"
        v-model="digitalVoucherDraftState.item_name"
        maxlength="50"
        data-cy="digital-voucher-name-input"
      />
      <span class="form-field-notice">max 50 characters</span>
    </div>

    <div class="column">
      <label>offers per customer</label>
      <select
        v-model="digitalVoucherDraftState.max_redemptions"
        data-cy="digital-voucher-max-redemptions-select"
      >
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
        <option value="5">5</option>
        <option value="10">10</option>
        <option value="-1">unlimited</option>
      </select>
    </div>

    <div class="column">
      <label>expiration date</label>
      <span class="form-field-notice">expires at 11:59 PM</span>
      <sb-date
        :flatpickr="flatPickrConfig"
        :include="['date']"
        placeholder="-"
        format="E, MMM d, y"
        mode="single"
        :value="expirationDate"
        :class="{ error: !digitalVoucherDraftState.expiration_date}"
        class="expiration-date"
        @update:value="changedExpiration"
        data-cy="digital-voucher-expiration-date-input" />
    </div>

    <div class="column">
      <label>display image</label>
      <span class="form-field-notice">displays on offers page</span>
      <span v-if="displayImage.error" class="form-field-notice error">{{ displayImage.error }}</span>
      <sb-loading-icon v-if="displayImage.loading" />
      <template v-else-if="displayImage.file">
          <img :src="displayImage.file.thumb" alt="display image" />
        <span class="remove-btn" @click="changedDisplayImage()" data-cy="digital-voucher-display-image-remove-button">remove</span>
      </template>
      <template v-else>
        <a 
          v-file-upload:png,gif,jpeg="changedDisplayImage"
          class="mb-1"
          data-cy="digital-voucher-display-image-button">
          <fai :icon="['fas', 'camera']" />
        </a>
      </template>
    </div>

    <div class="column">
      <label>voucher image</label>
      <span class="form-field-notice">displays on offer redemption</span>
      <span v-if="voucherImage.error" class="form-field-notice error">{{ voucherImage.error }}</span>
      <sb-loading-icon v-if="voucherImage.loading" />
      <template v-else-if="voucherImage.file">
          <img :src="voucherImage.file.thumb" alt="voucher image" />
        <span class="remove-btn" @click="changedVoucherImage()" data-cy="digital-voucher-voucher-image-remove-button">remove</span>
      </template>
      <template v-else>
        <a 
          v-file-upload:png,gif,jpeg="changedVoucherImage"
          class="mb-1"
          data-cy="digital-voucher-voucher-image-button">
          <fai :icon="['fas', 'camera']" />
        </a>
      </template>
    </div>
  </div>
</template>

<script>
import { attachmentAPI } from '../../../../services/api/attachmentAPI.js';
import { format } from "date-fns";
import { mapGetters } from "vuex";
import { utcToZonedTime } from "date-fns-tz";

export default {
  props: {
    digitalVoucherDraftState: Object,
    campaign: Object,
  },

  data() {
    return {
      displayImage: {
        file: null,
        loading: false,
        error: null
      },
      voucherImage: {
        file: null,
        loading: false,
        error: null
      },
      error: null,
      expirationDate: null,
    }
  },

  name: "digital-voucher-form",

  created() {
    this.digitalVoucherDraftState.type = null;
    this.expirationDate = !!this.digitalVoucherDraftState.expiration_date ? utcToZonedTime(this.digitalVoucherDraftState.expiration_date, this.currentMerchant.merchant_timezone) : null;
    this.refreshAttachments()
  },

  methods: {
    changedExpiration(selectedDate) {
      this.expirationDate = selectedDate;
      const formattedDate = selectedDate.length === 0 ? null : format(selectedDate, 'y-MM-dd');
      this.digitalVoucherDraftState.expiration_date = formattedDate;
      this.digitalVoucherDraftState.expiration_date_local = formattedDate;
    },

    async changedDisplayImage(file) {
      this.displayImage.loading = true;
      this.displayImage.error = null;

      try {
        if(!file && this.displayImage.file) {
          await attachmentAPI.delete(this.displayImage.file.uuid);
          this.digitalVoucherDraftState.has_preload_image = false;
        } else {
          await attachmentAPI.create(this.digitalVoucherDraftState.id, 'preload', 'Reward', file);
          this.digitalVoucherDraftState.has_preload_image = true;
        }
      } catch(e) {
        // TODO: Handle error here
        this.error = e
      }

      await this.refreshAttachments();
      this.displayImage.loading = false;
    },

    async changedVoucherImage(file) {
      this.voucherImage.loading = true;
      this.voucherImage.error = null;

      try {
        if(!file && this.voucherImage.file) {
          await attachmentAPI.delete(this.voucherImage.file.uuid);
          this.digitalVoucherDraftState.has_coupon_image = false;
        } else {
          await attachmentAPI.create(this.digitalVoucherDraftState.id, 'coupon', 'Reward', file);
          this.digitalVoucherDraftState.has_coupon_image = true;
        }
      } catch(e) {
        // TODO: Handle error here
        this.error = e
      }

      await this.refreshAttachments();
      this.voucherImage.loading = false;
    },

    async refreshAttachments() {
      this.displayImage.file = null;
      this.voucherImage.file = null;

      if(this.digitalVoucherDraftState.id !== null) {
        const attachments = await attachmentAPI.forReward(this.digitalVoucherDraftState.id, this.campaign.id, "Campaign")

        if(Array.isArray(attachments)) {
          const preloads = attachments.filter((attachment) => attachment.label === "preload");
          if(preloads.length > 0) {
            this.displayImage.file = preloads[preloads.length - 1];
            this.digitalVoucherDraftState.has_preload_image = true;
          }

          const coupons = attachments.filter((attachment) => attachment.label === "coupon");
          if(coupons.length > 0) {
            this.voucherImage.file = coupons[coupons.length - 1];
            this.digitalVoucherDraftState.has_coupon_image = true;
          }
        }
      }
    },
  },

  computed: {
    flatPickrConfig() {
      return {
        minDate: new Date()
      }
    },
    ...mapGetters(["currentMerchant"])
  }
}
</script>